/**
 * Working time, said in working units.
 *
 * WHY THIS EXISTS AS ONE THING. The SLA clock counts WORKING minutes — it
 * excludes nights, weekends and every hour a ticket sat paused. Rendering
 * that with a 24-hour day produces a number that is wrong by about 2.7×, and
 * wrong in the direction that makes everything look better: 12,857 working
 * minutes is 23.8 working days, and the daylighting screen displayed it as
 * "8.9d open" beside a finding that said "nothing has happened for 12
 * working days". A ticket cannot be silent for longer than it has existed,
 * so the screen was visibly self-contradicting, and a reader who spotted it
 * had no way to tell which half was wrong.
 *
 * One definition of a working day, in one place both the browser and the
 * server import.
 */

/** Nine hours, matching the seeded business calendar. */
export const WORKING_DAY_MINUTES = 540;

/**
 * Working minutes as a phrase, with the unit named.
 *
 * The unit is always stated. "23.8 days" invites comparison with a calendar
 * date and loses the argument; "23.8 working days" does not.
 */
export function workingTime(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  if (minutes < 0) return '—';
  const days = minutes / WORKING_DAY_MINUTES;
  if (days < 1) {
    const h = Math.round(minutes / 60);
    return h === 1 ? '1 working hour' : `${h} working hours`;
  }
  const rounded = days < 10 ? days.toFixed(1) : String(Math.round(days));
  return `${rounded} working days`;
}

/** The same figure, short enough for a badge: "23.8wd". */
export function workingTimeShort(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return '—';
  if (minutes < 0) return '—';
  const days = minutes / WORKING_DAY_MINUTES;
  if (days < 1) return `${Math.round(minutes / 60)}wh`;
  return `${days < 10 ? days.toFixed(1) : Math.round(days)}wd`;
}
